import { createTheme, parseThemeColor } from '@mantine/core';

// Components exposing size/radius/variant as props. `Input` receives them already
// resolved, so the defaults have to be declared on each outer component.
const INPUT_COMPONENTS = [
  'TextInput',
  'Textarea',
  'Select',
  'MultiSelect',
  'NumberInput',
  'InputBase',
  'DatePickerInput',
];

// Accepts both a Mantine color token (`blue.5`, `white`) and a plain CSS color.
const resolveColor = (color, theme) => (color ? parseThemeColor({ color, theme }).value : undefined);

/**
 * Mantine reads the input colors from these CSS variables, declared on the input
 * wrapper. Setting them inline on the wrapper overrides the variant defaults and
 * still lets the input apply its own `:focus` and error rules.
 */
const buildColorVars = (fields, theme) => ({
  '--input-bg': resolveColor(fields.background, theme),
  '--input-bd': resolveColor(fields.borderColor, theme),
  '--input-bd-focus': resolveColor(fields.focusBorderColor, theme),
  '--input-color': resolveColor(fields.textColor, theme),
  '--input-placeholder-color': resolveColor(fields.placeholderColor, theme),
});

// 'bottom' keeps the bottom border only, for the underlined field style.
const buildBorderWidth = (borderSides) => (borderSides === 'bottom' ? '0 0 1px 0' : undefined);

/**
 * Builds a Mantine theme that applies the configured field style to every input
 * inside the form, so the single field components stay untouched.
 *
 * @param {object} fields - The `fields` layout group
 */
export const buildFieldsTheme = (fields) => {
  const borderWidth = buildBorderWidth(fields.borderSides);

  return createTheme({
    components: {
      ...Object.fromEntries(
        INPUT_COMPONENTS.map((componentName) => [
          componentName,
          { defaultProps: { size: fields.size, radius: fields.radius, variant: fields.variant } },
        ])
      ),

      // Every field renders an `Input`, so one entry styles them all.
      Input: {
        styles: (theme, props) => ({
          // Colors are left out while the field is in error, so the validation styling
          // of mantine-overrides.css keeps precedence.
          wrapper: props.error ? {} : buildColorVars(fields, theme),
          input: { borderWidth },
        }),
      },

      // Same for the label of every field.
      InputWrapper: {
        styles: (theme) => ({
          label: {
            fontSize: fields.labelSize,
            fontWeight: fields.labelWeight,
            color: resolveColor(fields.labelColor, theme),
            textTransform: fields.labelCasing,
          },
        }),
      },
    },
  });
};
